import * as React from "react";
import { inject, observer } from "mobx-react";


import { Button, Row, Col, Spinner, Modal } from "react-bootstrap";

import { Navigate } from "react-router-dom";

import { Utils } from "../../common/utils";
import { UserStore } from "../../stores";
import * as Sentry from "@sentry/browser";


export interface IUserRegistrationProps {
    userStore?: UserStore
    activateToken: string
}

export interface IUserRegistrationState {
    password: string
    showPassword: boolean
    showModal: boolean
    redirect: boolean
    hasError: boolean
    checkingToken: boolean
    activateToken: string
}


@inject("userStore")
@observer
export class UserRegistration extends React.Component<IUserRegistrationProps, IUserRegistrationState> {

    state = {
        password: "",
        showPassword: false,
        showModal: false,
        redirect: false,
        hasError: false,
        checkingToken: true,
        activateToken: ""
    };

    blurDone = false;

    static getDerivedStateFromProps(props, state) {
        if (props.userStore && props.activateToken !== state.activateToken){
            return {activateToken: props.activateToken, checkingToken: true};
        }
        return { };
    }

    componentDidMount() {
        Sentry.addBreadcrumb({
            message: "Activation page rendered",
            data: {
                activationToken: this.props.activateToken,
                url: window.location.href,
            }
        });
        if (this.props.userStore){
            this.props.userStore.checkToken(this.props.activateToken).then(() => {
                this.setState({checkingToken: false});
            });
        }
    }

    componentDidUpdate(prevProps: Readonly<IUserRegistrationProps>, prevState: Readonly<IUserRegistrationState>) {
        if (this.props.userStore && prevState.activateToken !== this.state.activateToken) {
            this.props.userStore.checkToken(this.props.activateToken).then(() => {
                this.setState({checkingToken: false});
            });
        }
    }

    isValid = () => {
        const regexp = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/;
        const matches = this.state.password.match(regexp) || [];

        return matches && matches.length > 0 && this.state.password.length >= 10;
    };

    handleChange: React.ChangeEventHandler = (ev: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({password: ev.target.value}, () => {
            if(this.blurDone){
                this.updateHasError();
            }
        });
    };

    handleSubmit = () => {
        if (!this.props.userStore) return;
        this.props.userStore.setInitialPassword(this.state.password, this.props.activateToken);
    };

    handleFormSubmit = (ev) => {
        ev.preventDefault();
        if(this.isValid()){
            this.handleSubmit();
        }
    };

    inviteExpired = () => {
        if (this.props.userStore && this.props.userStore.tokenStatus) {
            return this.props.userStore.tokenStatus.expired;
        }
        return false;
    };

    isLoading = () => {
        if (this.props.userStore) {
            return this.state.checkingToken;
        }
        return false;
    };

    passwordPreviouslySet = () => {
        if (this.props.userStore && this.props.userStore.tokenStatus) {
            return this.props.userStore.tokenStatus.password_set;
        }
        return false;
    };

    toggleShowPassword = () => {
        this.setState({showPassword: !this.state.showPassword});
    };

    updateHasError = () => {
        if(this.isValid()){
            this.setState({hasError: false});
        } else {
            this.setState({hasError: true});
        }
    };

    handleBlur = () => {
        if(!this.blurDone){
            this.blurDone = true;
        }
        this.updateHasError();
    };


    handleModalClose = () => {
       this.setState({
           showModal: false,
           redirect: true
       });
    };

    renderPasswordReset = () => {
        return (
            <React.Fragment>
                <Row className="registration-container">
                    <Col md="6" className="left text-center">
                        <div className="logo"/>
                    </Col>
                    <Col md="6" className="right">
                        <form id="registration-form" onSubmit={this.handleFormSubmit}>
                            <div className="password-group">
                                <input
                                    className="password-input"
                                    placeholder="Set A Password"
                                    onChange={this.handleChange}
                                    onBlur={this.handleBlur}
                                    type={this.state.showPassword ? "text" : "password"}/>
                                <div className="password-mask-toggle"
                                     onClick={this.toggleShowPassword}>{this.state.showPassword ? "HIDE" : "SHOW"}</div>
                            </div>
                            <div className="text-muted"
                                style={{padding: "0px 10px"}}>{"Password must contain a minimum of 10 characters and at least one uppercase letter, at least one lowercase letter, and a number. Password should be unique and not shared with your school credentials."}</div>
                            <Button
                                type="submit"
                                variant="primary"
                                size="lg"
                                disabled={!this.isValid() || this.state.hasError || (this.props.userStore && this.props.userStore.isUpdating)}>
                                {this.props.userStore && this.props.userStore.isUpdating
                                    ? <Spinner className="green-spinner" animation={"border"} variant={"success"}/>
                                    : "Submit"
                                }
                            </Button>
                            <div
                                className="error-container">{this.props.userStore && this.props.userStore.errorMessage
                                ? <span>
                                    <i className="icon icon-exclamation-circle"/>
                                    {` ${this.props.userStore.errorMessage}`}
                                </span>
                                : ""}
                            </div>
                        </form>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <div id="copy-div" className="copyright">© 2017 - {Utils.getCurrentYear()} Enflux. All Rights Reserved</div>
                    </Col>
                </Row>
                <Modal
                    show={this.inviteExpired()}
                    contentClassName="modal-container confirmation">
                    <div className="alert-icon"><i className="icon icon-exclamation-circle" /></div>
                    <div className="content">
                        This invitation has expired. Please contact your Enflux administrator for a new invitation.
                    </div>
                    <div className="alert-action">
                        <Button variant="primary" size="lg" onClick={ this.handleModalClose } id="okExpired">
                            OK
                        </Button>
                    </div>
                </Modal>
            </React.Fragment>
        );
    };

    render() {
        // no one likes a flasher
        if(this.isLoading()){
            return <Spinner className="green-spinner" animation={"border"} variant={"success"} />;
        }
        if(this.state.redirect || this.passwordPreviouslySet()) {
            return <Navigate to="/" />;
        }
        return this.renderPasswordReset();
    }
}
